<template>
  <section class="bg-white text-gray-900">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <!-- Header / Navbar part within the hero -->
      <header class="grid grid-cols-12 items-center mb-16 lg:mb-16">
        <div class="col-span-6 flex items-center space-x-2">
          <LogoMain />
          <span class="font-bold text-xl">Better Insights</span>
        </div>
        <div class="col-span-6 flex justify-end items-center space-x-12">
          <router-link
            to="/blog"
            class="hidden lg:block text-gray-600 hover:text-emerald-500 transition-colors font-heading text-lg font-medium"
          >
            Blog
          </router-link>
          <ButtonMain class="hidden lg:block"
            ><a
              href="https://calendly.com/rositano-tony/30-minute-meeting"
              target="_blank"
              >Book a call</a
            ></ButtonMain
          >
          <HamburgerMenu class="lg:hidden" />
        </div>
      </header>

      <!-- Main Hero Content -->
      <div class="grid grid-cols-12 items-center gap-8">
        <!-- Left Column -->
        <div
          class="col-span-12 lg:col-span-6 text-center lg:text-left mb-10 lg:mb-0"
        >
          <div
            ref="lottieIconContainer"
            class="w-56 h-56 mx-auto mb-4 lg:hidden"
          ></div>
          <div
            class="hidden lg:flex items-center justify-center lg:justify-start space-x-2 text-gray-500 mb-4"
          >
            <ShopifyIcon class="h-6 w-6" />
            <span>Specialising in Shopify Brand Owners</span>
          </div>
          <!-- 
          <h1
            class="text-5xl md:text-6xl lg:text-7xl font-heading font-semibold mb-6 leading-tight"
          >
            Better data, <br class="hidden sm:block" />
            to maximize
            <span class="text-primary-green relative inline-block"> ROI </span>
          </h1> -->

          <h1
            class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-semibold mb-6 leading-tight lg:leading-[74px]"
          >
            Better data,
            <span class="text-primary-green relative inline-block">
              BIGGER ROI
              <svg
                class="hidden md:block md:absolute md:-bottom-4 md:left-0 md:w-full"
                viewBox="0 0 385 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M384 13C255.333 -1.66666 126.667 -1.66666 -2 13"
                  stroke="#3DAB81"
                  stroke-width="3"
                  stroke-linecap="round"
                  class="path-animation"
                />
              </svg>
            </span>
            <br />
            <span class="text-3xl sm:text-5xl md:text-6xl lg:text-5xl">
              for ecommerce
            </span>
            <!-- <br class="hidden sm:block" /> -->

            <!-- <span class="text-primary-green relative inline-block"> ROI </span> -->
          </h1>

          <!-- <p class="hidden lg:block text-lg text-gray-600 mb-10">
            Smart brands put data first by tracking the right events and the
            right KPIs
          </p> -->

          <!-- Value Propositions Section -->
          <div class="mt-10 lg:mt-24 lg:pt-10 lg:border-t border-gray-200">
            <div class="grid grid-cols-12 gap-8">
              <div class="col-span-12 sm:col-span-6 text-left">
                <div class="flex items-center space-x-3 mb-2">
                  <svg
                    class="w-5 h-5 text-primary-green flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <h3 class="font-heading font-semibold text-gray-900">
                    Complete Tracking Setup
                  </h3>
                </div>
                <p class="text-gray-600 ml-8">
                  Full tracking implementation with Google Analytics 4 & Social
                  Media Pixels
                </p>
              </div>
              <div class="col-span-12 sm:col-span-6 text-left">
                <div class="flex items-center space-x-3 mb-2">
                  <svg
                    class="w-5 h-5 text-primary-green flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <h3 class="font-heading font-semibold text-gray-900">
                    Custom Dashboards
                  </h3>
                </div>
                <p class="text-gray-600 ml-8">
                  Personalized reporting dashboard focused on key metrics
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Illustration -->
        <div
          class="hidden lg:flex col-span-12 lg:col-span-6 items-center justify-center"
        >
          <div
            ref="lottieContainer"
            class="relative w-full max-w-lg lg:max-w-none h-96 lg:h-[650px]"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import lottie from "lottie-web";
import ButtonMain from "@/components/shared/ButtonMain.vue";
import LogoMain from "@/components/shared/LogoMain.vue";
import ShopifyIcon from "@/components/shared/ShopifyIcon.vue";
import HamburgerMenu from "@/components/shared/HamburgerMenu.vue";

const lottieContainer = ref(null);
const lottieIconContainer = ref(null);

onMounted(() => {
  if (lottieContainer.value) {
    lottie.loadAnimation({
      container: lottieContainer.value,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "https://cdn.lottielab.com/l/37zRtMrDKCLSG6.json",
    });
  }

  if (lottieIconContainer.value) {
    lottie.loadAnimation({
      container: lottieIconContainer.value,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "https://cdn.lottielab.com/l/9aVvN4GBeLQ5rD.json",
    });
  }
});
</script>

<style scoped>
.path-animation {
  stroke-dasharray: 400;
  stroke-dashoffset: 400;
  animation: draw 1s ease forwards;
}

@keyframes draw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
